// `import type` is erased at compile time, so this module stays free of the
// i18n/router side-effects that importing ProtectedRoute's runtime would pull in.
import type { PostgresRole } from '@/shared/components/ProtectedRoute';

/**
 * The RBAC role(s) that make a user an "Account Manager" — someone who can own a quote.
 *
 * VOCABULARY TRAP — the name of this constant and its value disagree ON PURPOSE:
 *
 *   Everyone at ATEK says "Account Manager".  The database says `sales_manager`.
 *   They are the same thing. There is NO `account_manager` RBAC role.
 *
 * The UI translates at the boundary — it renders "Account Manager" / "Assigned
 * account manager" on screen while the endpoint (`/v4/accounts/sales-managers`),
 * the hook (`useSalesManagers`), the form field (`formData.sales_manager`) and
 * the column (`sales_manager_id`) all say sales_manager. This constant is that
 * translation, made explicit and kept in one place.
 *
 * Do NOT add 'account_manager' here just because it matches the spoken word.
 * It is not a real role: it has no `role_definitions` row, and its only holder
 * in production is a QA fixture (qa.tester@atek.io). The name comes from the
 * account-CONTACT vocabulary (Contractual/Technical/IT Manager), a different
 * domain that happens to reuse the word. Commit 9197fdd04 made exactly that
 * mistake and emptied the production Account Manager dropdown.
 *
 * `sales_manager` is load-bearing (CRM + AM-dashboard route gates, quotes.create,
 * ATEK-staff status) — see rest_server/dao/accounts/list.js getDistinctSalesManagers,
 * which carries the full explanation and must stay in sync with this list.
 *
 * If the role is ever renamed to match how people actually speak, this array is
 * the seam: hold both values during the migration, then drop 'sales_manager'.
 */
export const ACCOUNT_MANAGER_ROLES: PostgresRole[] = ['sales_manager'];
